"use strict";
/*******************************************************************************

    Contains a class to manage multiple accounts.

    Copyright:
        Copyright (c) 2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccountContainer = exports.Account = exports.AccountMode = void 0;
var Amount_1 = require("../common/Amount");
var KeyPair_1 = require("../common/KeyPair");
var Types_1 = require("../net/response/Types");
var EventDispatcher_1 = require("./EventDispatcher");
var Types_2 = require("./Types");
var WalletBalance_1 = require("./WalletBalance");
var WalletUTXOProvider_1 = require("./WalletUTXOProvider");
/**
 * Account Mode
 */
var AccountMode;
(function (AccountMode) {
    AccountMode[AccountMode["READ_ONLY"] = 0] = "READ_ONLY";
    AccountMode[AccountMode["SUDO"] = 1] = "SUDO";
})(AccountMode = exports.AccountMode || (exports.AccountMode = {}));
/**
 * Class with account address, secret key and balance.
 */
var Account = /** @class */ (function (_super) {
    __extends(Account, _super);
    /**
     * Constructor
     * @param owner The instance of AccountContainer
     * @param name The name of account
     * @param key The public key or secret key of account
     */
    function Account(owner, name, key) {
        var _this = _super.call(this) || this;
        _this.owner = owner;
        _this._name = name;
        if (key instanceof KeyPair_1.PublicKey) {
            _this._address = key;
            _this._secret = undefined;
        }
        else {
            _this._secret = key;
            _this._address = new KeyPair_1.PublicKey(_this._secret.scalar.toPoint());
        }
        _this._balance = new WalletBalance_1.WalletBalance(_this._address.toString());
        _this.spendableUTXOProvider = new WalletUTXOProvider_1.WalletUTXOProvider(_this._address, owner.client, Types_1.BalanceType.spendable);
        _this.frozenUTXOProvider = new WalletUTXOProvider_1.WalletUTXOProvider(_this._address, owner.client, Types_1.BalanceType.frozen);
        return _this;
    }
    Object.defineProperty(Account.prototype, "name", {
        /**
         * The name of account
         */
        get: function () {
            return this._name;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Account.prototype, "address", {
        /**
         * The public key of account
         */
        get: function () {
            return this._address;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Account.prototype, "secret", {
        /**
         * The secret key of account
         */
        get: function () {
            return this._secret;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Account.prototype, "balance", {
        /**
         * The balance of account
         */
        get: function () {
            return this._balance;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Account.prototype, "mode", {
        /**
         * The mode of account
         */
        get: function () {
            return this.secret !== undefined ? AccountMode.SUDO : AccountMode.READ_ONLY;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Set the secret key.
     * @param value The secret key to be set
     */
    Account.prototype.setSecret = function (value) {
        var pk = new KeyPair_1.PublicKey(value.scalar.toPoint());
        if (pk.toString() === this.address.toString()) {
            this._secret = value;
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE, this);
            return true;
        }
        else {
            return false;
        }
    };
    /**
     * Check the balance.
     */
    Account.prototype.checkBalance = function (is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        return __awaiter(this, void 0, void 0, function () {
            var res;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.owner.client.getBalance(this.address)];
                    case 1:
                        res = _a.sent();
                        if (res.code === Types_2.WalletResultCode.Success && res.data !== undefined) {
                            if (!Amount_1.Amount.equal(this._balance.balance, res.data.balance) ||
                                !Amount_1.Amount.equal(this._balance.spendable, res.data.spendable) ||
                                !Amount_1.Amount.equal(this._balance.frozen, res.data.frozen) ||
                                !Amount_1.Amount.equal(this._balance.locked, res.data.locked) ||
                                !this._balance.enable) {
                                this._balance = new WalletBalance_1.WalletBalance(this._address.toString(), Amount_1.Amount.make(res.data.balance), Amount_1.Amount.make(res.data.spendable), Amount_1.Amount.make(res.data.frozen), Amount_1.Amount.make(res.data.locked), true);
                                if (is_dispatch)
                                    this.dispatchEvent(EventDispatcher_1.Event.CHANGE_BALANCE, this);
                                else
                                    this.owner.onAccountChangeBalance(EventDispatcher_1.Event.CHANGE_BALANCE, this);
                            }
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns an array of all frozen UTXOs for addresses already set
     */
    Account.prototype.getFrozenUTXOs = function () {
        return __awaiter(this, void 0, void 0, function () {
            var frozenUtxos, utxo_res, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, this.checkBalance()];
                    case 1:
                        _a.sent();
                        this.frozenUTXOProvider.clear();
                        return [4 /*yield*/, this.frozenUTXOProvider.getUTXO(this.balance.frozen)];
                    case 2:
                        utxo_res = _a.sent();
                        if (utxo_res.code !== Types_2.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: utxo_res.code, message: utxo_res.message }];
                        }
                        frozenUtxos = utxo_res.data;
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestUTXO, message: Types_2.WalletMessage.FailedRequestUTXO }];
                    case 4: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.Success,
                            message: Types_2.WalletMessage.Success,
                            data: frozenUtxos,
                        }];
                }
            });
        });
    };
    return Account;
}(EventDispatcher_1.EventDispatcher));
exports.Account = Account;
/**
 * Class to manage multiple accounts.
 */
var AccountContainer = /** @class */ (function (_super) {
    __extends(AccountContainer, _super);
    /**
     * Constructor
     */
    function AccountContainer(client) {
        var _this = _super.call(this) || this;
        _this._items = [];
        _this._balance = new WalletBalance_1.WalletBalance();
        _this._selected_index = -1;
        _this.client = client;
        return _this;
    }
    Object.defineProperty(AccountContainer.prototype, "items", {
        /**
         * The array where the account is saved.
         */
        get: function () {
            return this._items;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "balance", {
        /**
         * The balance of all accounts
         */
        get: function () {
            return this._balance;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "selected_index", {
        /**
         * The getter of selected_index
         */
        get: function () {
            return this._selected_index;
        },
        /**
         * The setter of selected_index
         */
        set: function (value) {
            if (value < 0 || value >= this._items.length)
                return;
            this._selected_index = value;
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SELECTED, this.selected_index);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "selected_account", {
        /**
         * The getter of selected_account
         */
        get: function () {
            if (this._selected_index < 0)
                return null;
            if (this._selected_index >= this._items.length)
                return null;
            return this._items[this._selected_index];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "un_select_accounts", {
        /**
         * The getter of un_selected_accounts
         */
        get: function () {
            if (this._selected_index < 0)
                return null;
            if (this._items.length <= 1)
                return null;
            var selAccount = this.selected_account;
            if (selAccount) {
                return this._items.filter(function (m) { return !KeyPair_1.PublicKey.equal(m.address, selAccount.address); });
            }
            else {
                return null;
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Add one account.
     * @param name The name of the account
     * @param key  The public key or secret key of the account
     * @param is_dispatch Set whether an event for changes will occur or not.
     * @param change_select Set whether an event will occur or not. The default value is true.
     */
    AccountContainer.prototype.add = function (name, key, is_dispatch, change_select) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        if (change_select === void 0) { change_select = true; }
        var account = this._items.find(function (m) { return m.name === name; });
        if (account !== undefined)
            return undefined;
        if (key instanceof KeyPair_1.PublicKey) {
            account = this._items.find(function (m) { return KeyPair_1.PublicKey.equal(m.address, key); });
            if (account !== undefined)
                return undefined;
        }
        if (key instanceof KeyPair_1.SecretKey) {
            var pk_1 = new KeyPair_1.PublicKey(key.scalar.toPoint());
            account = this._items.find(function (m) {
                if (m.secret !== undefined) {
                    return KeyPair_1.SecretKey.equal(m.secret, key);
                }
                else {
                    return KeyPair_1.PublicKey.equal(m.address, pk_1);
                }
            });
            if (account !== undefined)
                return undefined;
        }
        var changed = false;
        account = new Account(this, name, key);
        this._items.push(account);
        this.attachEventListener(account);
        if (this.selected_index < 0) {
            this._selected_index = 0;
            changed = true;
        }
        if (change_select) {
            this._selected_index = this._items.length - 1;
            changed = true;
        }
        if (is_dispatch)
            this.dispatchEvent(EventDispatcher_1.Event.ADDED, account);
        if (is_dispatch)
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE);
        if (changed) {
            if (is_dispatch)
                this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SELECTED, this.selected_index);
        }
        return account;
    };
    /**
     * Remove one account.
     * @param name Name of the account to be removed.
     * @param is_dispatch Set whether an event for changes will occur or not.
     */
    AccountContainer.prototype.remove = function (name, is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        var findIdx = this._items.findIndex(function (m) { return m.name === name; });
        if (findIdx < 0)
            return undefined;
        var account = this._items[findIdx];
        this._items.splice(findIdx, 1);
        this.detachEventListener(account);
        var change_selected = false;
        if (findIdx <= this.selected_index) {
            if (this._items.length === 0) {
                this._selected_index = -1;
                change_selected = true;
            }
            else {
                if (this.selected_index > 0) {
                    this.selected_index--;
                    change_selected = true;
                }
            }
        }
        if (is_dispatch)
            this.dispatchEvent(EventDispatcher_1.Event.REMOVED, account);
        if (is_dispatch)
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE);
        if (change_selected) {
            if (is_dispatch)
                this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SELECTED, this.selected_index);
        }
        return account;
    };
    /**
     * Find the account with the name or address of the account.
     * @param key The name or address of the account.
     */
    AccountContainer.prototype.find = function (key) {
        var account = this._items.find(function (m) { return m.name === key; });
        if (account !== undefined)
            return account;
        if (KeyPair_1.PublicKey.validate(key) === "") {
            var public_key_1 = new KeyPair_1.PublicKey(key);
            account = this._items.find(function (m) { return KeyPair_1.PublicKey.equal(m.address, public_key_1); });
            if (account !== undefined)
                return account;
        }
        if (KeyPair_1.SecretKey.validate(key) === "") {
            var secret_key_1 = new KeyPair_1.SecretKey(key);
            var public_key_2 = new KeyPair_1.PublicKey(secret_key_1.scalar.toPoint());
            account = this._items.find(function (m) {
                if (m.secret !== undefined) {
                    return KeyPair_1.SecretKey.equal(m.secret, secret_key_1);
                }
                else {
                    return KeyPair_1.PublicKey.equal(m.address, public_key_2);
                }
            });
            if (account !== undefined)
                return account;
        }
        else {
            account = this._items.find(function (m) { return m.secret !== undefined && m.secret.toString(false) === key; });
            if (account !== undefined)
                return account;
        }
        return undefined;
    };
    /**
     * Find the account by name
     * @param name The account name
     */
    AccountContainer.prototype.findByName = function (name) {
        return this._items.find(function (m) { return m.name === name; });
    };
    /**
     * Find the account by public key
     * @param pk The public key
     */
    AccountContainer.prototype.findByPublicKey = function (pk) {
        return this._items.find(function (m) { return KeyPair_1.PublicKey.equal(m.address, pk); });
    };
    /**
     * Find the account by secret key
     * @param sk The secret key
     */
    AccountContainer.prototype.findBySecretKey = function (sk) {
        var pk = new KeyPair_1.PublicKey(sk.scalar.toPoint());
        return this._items.find(function (m) {
            if (m.secret !== undefined) {
                return KeyPair_1.SecretKey.equal(m.secret, sk);
            }
            else {
                return KeyPair_1.PublicKey.equal(m.address, pk);
            }
        });
    };
    /**
     * Clear all accounts
     * @param is_dispatch Set whether an event for changes will occur or not.
     */
    AccountContainer.prototype.clear = function (is_dispatch) {
        var _this = this;
        if (is_dispatch === void 0) { is_dispatch = true; }
        this._items.forEach(function (m) {
            if (is_dispatch)
                _this.dispatchEvent(EventDispatcher_1.Event.REMOVED, m);
            _this.detachEventListener(m);
        });
        this._selected_index = -1;
        this._items.length = 0;
        if (is_dispatch) {
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE);
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SELECTED, this.selected_index);
        }
    };
    Object.defineProperty(AccountContainer.prototype, "length", {
        /**
         * Return the length
         */
        get: function () {
            return this._items.length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "addresses", {
        /**
         * Only the public address of the account is extracted and returned to the array.
         */
        get: function () {
            return this._items.map(function (m) { return new KeyPair_1.PublicKey(m.address.point); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "names", {
        /**
         * Only the name of the account is extracted and returned to the array.
         */
        get: function () {
            return this._items.map(function (m) { return m.name; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "readonly_accounts", {
        /**
         * Return only accounts that don't have a secret key.
         */
        get: function () {
            return this._items.filter(function (m) { return m.mode === AccountMode.READ_ONLY; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "sudo_accounts", {
        /**
         * Return only accounts that have a secret key.
         */
        get: function () {
            return this._items.filter(function (m) { return m.mode === AccountMode.SUDO; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AccountContainer.prototype, "sudo_length", {
        /**
         * Return the number of accounts with the secret key.
         */
        get: function () {
            return this._items.reduce(function (prev, m) {
                return prev + (m.mode === AccountMode.SUDO ? 1 : 0);
            }, 0);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Check the balance of all accounts.
     */
    AccountContainer.prototype.checkBalance = function (calculate) {
        if (calculate === void 0) { calculate = true; }
        this._items.forEach(function (m) { return m.checkBalance(); });
        if (calculate) {
            this.calculateTotalBalance();
            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_BALANCE, this);
        }
    };
    /**
     * Attach the event listener
     * @param account
     */
    AccountContainer.prototype.attachEventListener = function (account) {
        account.addEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, this.onAccountChangeBalance, this);
        account.addEventListener(EventDispatcher_1.Event.CHANGE, this.onAccountChangeSecretKey, this);
    };
    /**
     * Detach the event listener
     * @param account
     */
    AccountContainer.prototype.detachEventListener = function (account) {
        account.removeEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, this.onAccountChangeBalance, this);
        account.removeEventListener(EventDispatcher_1.Event.CHANGE, this.onAccountChangeSecretKey, this);
    };
    /**
     * The event listener on changed the balance
     * @param type The event type
     * @param account The account with changed balance
     */
    AccountContainer.prototype.onAccountChangeBalance = function (type, account) {
        this.calculateTotalBalance();
        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_BALANCE, this);
    };
    /**
     * Sum all account balances.
     */
    AccountContainer.prototype.calculateTotalBalance = function () {
        var balance = Amount_1.Amount.make(0);
        var spendable = Amount_1.Amount.make(0);
        var frozen = Amount_1.Amount.make(0);
        var locked = Amount_1.Amount.make(0);
        for (var _i = 0, _a = this._items; _i < _a.length; _i++) {
            var elem = _a[_i];
            if (!elem.balance.enable)
                continue;
            balance = Amount_1.Amount.add(balance, elem.balance.balance);
            spendable = Amount_1.Amount.add(spendable, elem.balance.spendable);
            frozen = Amount_1.Amount.add(frozen, elem.balance.frozen);
            locked = Amount_1.Amount.add(locked, elem.balance.locked);
        }
        this._balance = new WalletBalance_1.WalletBalance("", balance, spendable, frozen, locked, true);
    };
    /**
     * The event listener on changed the secret key
     * @param type The event type
     * @param account The account with changed the secret key
     */
    AccountContainer.prototype.onAccountChangeSecretKey = function (type, account) {
        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SECRET_KEY, account);
    };
    /**
     * Get a string to save it. The secret key is not saved, but only the public key is saved.
     */
    AccountContainer.prototype.toString = function () {
        return JSON.stringify(this._items.map(function (m) {
            return {
                name: m.name,
                address: m.address.toString(),
            };
        }));
    };
    /**
     * Restores data stored in a string.
     * @param data
     */
    AccountContainer.prototype.fromString = function (data) {
        try {
            var list = JSON.parse(data);
            this._selected_index = -1;
            this._items.length = 0;
            for (var _i = 0, list_1 = list; _i < list_1.length; _i++) {
                var elem = list_1[_i];
                if (elem.name !== undefined && elem.address !== undefined) {
                    var account = new Account(this, elem.name, new KeyPair_1.PublicKey(elem.address));
                    this._items.push(account);
                    this.attachEventListener(account);
                    this.dispatchEvent(EventDispatcher_1.Event.ADDED, account);
                }
            }
            if (this._items.length > 0)
                this._selected_index = 0;
        }
        catch (e) {
            return false;
        }
        return true;
    };
    return AccountContainer;
}(EventDispatcher_1.EventDispatcher));
exports.AccountContainer = AccountContainer;
//# sourceMappingURL=Account.js.map